import { logError, logMessage } from './sentry';
import { hashString } from './utils';
import { sendOCSTrackingEvent } from './OCSTrackingEvent';

export function getMraidVersion() {
  return parseFloat(window.mraid.getVersion());
}

export function getMaidUrl() {
  return 'mraid.js';
}

/**
 * for hooks prefer => useMRAIDViewability()
 * or use => useMraidListener()
 */
export function waitForViewability(cb: (isViewable: boolean) => void) {
  cb(
    window.mraid.isViewable() === true || window.mraid.isViewable() === 'true'
  );

  const listener = (isViewable: boolean) => {
    if (isViewable) {
      cb(true);
    } else {
      cb(
        window.mraid.isViewable() === true ||
          window.mraid.isViewable() === 'true'
      );
    }
  };

  // Override toString method to fix Mopub addEventListerner implementation
  // Mopub use String(listener) to compare if the listener function exists or not
  // We hash the callback function to ensure that this mecanism is still working but
  // we only consider that the callback function must be different
  listener.toString = () => `fct${hashString(String(cb))}`;
  window.mraid.addEventListener('viewableChange', listener);

  if (getMraidVersion() >= 3) {
    window.mraid.addEventListener(
      'exposureChange',
      (exposedPercentage: number) => {
        cb(exposedPercentage >= 50);
      }
    );
  }
}

/**
 * for hooks prefer => useMRAIDExposureChange()
 * or use => useMraidListener()
 */
export function waitForExposure(cb: MRAIDExposureChangeListener) {
  if (getMraidVersion() >= 3) {
    window.mraid.addEventListener(
      'exposureChange',
      (
        exposedPercentage: number,
        visibleRectangle?: MRAIDPosition,
        occlusionRectangles?: MRAIDPosition[]
      ) => {
        cb(exposedPercentage, visibleRectangle, occlusionRectangles);
      }
    );
  }
}

/**
 * for hooks prefer => useMRAIDStateChange()
 * or use => useMraidListener()
 */
export function listenState(cb: (mraidState: MRAIDState) => void) {
  window.mraid.addEventListener('stateChange', cb);
  cb(window.mraid.getState());
}

/**
 * for hooks prefer => useMRAIDStateChange()
 * or use => useMraidListener()
 * - We should rewrite this method with it
 */
export function waitForReady(
  cb: () => void,
  onEventListening: () => void = () => {}
) {
  if (window.mraid.getState() === 'loading') {
    onEventListening();
    window.mraid.addEventListener('ready', cb);
  } else {
    cb();
  }
}

export function unloadFormat(errorMessage?: string): void {
  if (errorMessage) {
    logMessage(errorMessage);
  }
  if (getMraidVersion() >= 3 && window.mraid.unload) {
    window.mraid.unload();
  } else {
    window.mraid.close();
  }
}

export function unloadFormatOnReady(errorMessage?: string): void {
  waitForReady(() => unloadFormat(errorMessage));
}

export function triggerOgyOnAdClicked() {
  if (window.mraid.ogyOnAdClicked) {
    window.mraid.ogyOnAdClicked();
  }
}

export function triggerOnAdLoaded() {
  window.mraid.ogyOnAdLoaded && window.mraid.ogyOnAdLoaded();
}

export function listenCallTagEvent(callTagEvent: any) {
  switch (callTagEvent.detail?.method) {
    case 'ogySendMonitoringLogs':
      const sendMonitoringLogsEvent: MRAIDOgySendMonitoringLogs = e => {
        e.extra = { ...e.extra, mraidMethod: 'ogySendMonitoringLogs' };
        if (e.message instanceof Error || e.message instanceof ErrorEvent) {
          logError(e.message, e.extra);
          return;
        }
        logMessage(e.message, e.extra, e.type);
      };
      sendMonitoringLogsEvent(callTagEvent.detail.args);
      break;
    case 'ogySendTrackingEvent':
      const sendTrackingEvent: MRAIDOgySendTrackingEvent = e => {
        if (e.event.indexOf('OCS_') !== -1) {
          sendOCSTrackingEvent(e.event, e.parameters);
        }
      };
      sendTrackingEvent(callTagEvent.detail.args);
      break;
  }
}
